import { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Coupon } from '../types/coupons';
import { format } from 'date-fns';
import { useCouponAuth } from '../hooks/use-coupon-auth';
import { useClipCoupon } from '../hooks/use-clip-coupon';
import { useClippedCoupons } from '../hooks/use-clipped-coupons';
import { useClipToast } from '../context/ClipToastContext';
import { CouponAuthModal } from './CouponAuthModal';
import { CouponDetailSheet } from './CouponDetailSheet';
import { trackCouponClip, trackCouponView } from '@/lib/analytics';
import { IconCheck, IconLoader4, IconPlus, IconSparkles } from '@tabler/icons-react';

interface CouponCardProps {
  coupon: Coupon;
}

const formatDate = (date: string | undefined): string | null => {
  if (!date) return null;
  try {
    return format(new Date(date), 'MM/dd/yyyy');
  } catch {
    return null;
  }
};

export const CouponCard = ({
  coupon,
}: CouponCardProps) => {
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [detailSheetOpen, setDetailSheetOpen] = useState(false);

  const { isAuthenticated } = useCouponAuth();
  const { showClipToast } = useClipToast();
  const clipCouponMutation = useClipCoupon({
    onSuccess: () => {
      showClipToast('success');
      trackCouponClip({
        couponId: coupon.offerId || coupon.id || '',
        couponTitle: coupon.title || '',
        couponValue: coupon.value || '',
        couponCategory: coupon.category,
      });
    },
    onError: (error) => showClipToast('error', error.message || 'Unable to clip coupon.'),
  });
  const { data: clippedCoupons = [] } = useClippedCoupons();

  const expirationDate = formatDate(coupon.expirationDate);

  const isClipped = coupon.isClipped || clippedCoupons.some(
    (c) => c.id === coupon.id
  );
  const isLoading = clipCouponMutation.isPending;

  const handleClip = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isLoading) return;

    if (isAuthenticated) {
      const couponId = coupon.id;
      if (couponId) {
        clipCouponMutation.mutate(couponId);
      }
    } else {
      setAuthModalOpen(true);
    }
  };

  return (
    <>
      <Card
        className="group flex flex-col h-full bg-white hover:shadow-lg transition-all duration-200 border hover:border-primary/20 cursor-pointer overflow-hidden"
        onClick={() => {
          setDetailSheetOpen(true);
          trackCouponView({
            couponId: coupon.offerId || coupon.id || '',
            couponTitle: coupon.title || '',
            couponValue: coupon.value || '',
            couponCategory: coupon.category,
          });
        }}
      >
        <div className="relative">
          {coupon.category && (
            <Badge className="absolute top-0 right-0 z-10 bg-brand-primary-500 hover:bg-brand-primary-400 text-white text-[10px] font-semibold px-2 py-1 uppercase tracking-wide rounded-none rounded-bl-lg badge-sm">
              {coupon.category}
            </Badge>
          )}

          <div className="w-full flex items-center justify-center p-4 pt-8 bg-white">
            {coupon.imageUrl ? (
              <img
                src={coupon.imageUrl}
                alt={coupon.title || 'Coupon'}
                className="w-[164px] h-[164px] object-contain"
                loading="lazy"
              />
            ) : (
              <div className="w-[164px] h-[164px] bg-gradient-to-br from-primary/10 via-primary/5 to-background flex items-center justify-center rounded">
                <IconSparkles className="h-10 w-10 text-primary/30" />
              </div>
            )}
          </div>
        </div>

        <CardContent className="flex-1 flex flex-col p-4 pt-2 relative">
          <div className="flex-1 space-y-1">
            <p className="text-lg font-bold text-red-500 line-clamp-2">
              {coupon.title}
            </p>

            {(coupon.brand || coupon.subtitle) && (
              <p className="font-bold text-sm text-foreground line-clamp-1">
                {coupon.brand || coupon.subtitle}
              </p>
            )}

            {coupon.description && (
              <p className="text-xs text-muted-foreground line-clamp-2">
                {coupon.description}
              </p>
            )}
          </div>

          <div className="flex items-end justify-between mt-3">
            {expirationDate && (
              <p className="text-xs text-muted-foreground">
                Offer expires on<br />{expirationDate}
              </p>
            )}

            <button
              onClick={handleClip}
              disabled={isLoading}
              className={`w-14 h-14 rounded-tl-full flex items-center justify-center pl-2 pt-2 transition-colors absolute bottom-0 right-0 ${
                isClipped
                  ? 'bg-emerald-500 hover:bg-emerald-600'
                  : 'bg-red-400 hover:bg-red-500'
              }`}
            >
              {isLoading ? (
                <IconLoader4 className="h-5 w-5 text-white animate-spin" />
              ) : isClipped ? (
                <IconCheck className="h-6 w-6 text-white" />
              ) : (
                <IconPlus className="h-6 w-6 text-white" />
              )}
            </button>
          </div>
        </CardContent>
      </Card>

      <CouponAuthModal
        coupon={coupon}
        open={authModalOpen}
        onOpenChange={setAuthModalOpen}
      />

      <CouponDetailSheet
        coupon={coupon}
        open={detailSheetOpen}
        onOpenChange={setDetailSheetOpen}
      />
    </>
  );
};
